import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import ReminderModel from './ReminderModel';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import { Reminders } from '../../../../types/Reminder';
import dayjs from 'dayjs';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

const Reminder: React.FC = () => {
  const [reminder, setReminders] = useState<Reminders[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Reminders | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchReminders = async () => {
    try {
      const data = await apiRequest<Reminders[]>({
        method: 'get',
        url: 'reminders',
      });
      setReminders(data);
    } catch (error) {
      showError('Failed to fetch reminder');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchReminders();
  }, []);

  const handleEditClick = (record: Reminders) => {
    setIsEditMode(true);
    setInitialValues({
      reminder_id: record.reminder_id,
      reminder_ledger_id: record.reminder_ledger_id,
      reminder_trial: record.reminder_trial,
      reminder_delivery: record.reminder_delivery,
      ledger_first_name: record.ledger_first_name,
      ledger_last_name: record.ledger_last_name,
      reminder_status: record.reminder_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchReminders();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (reminder_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-reminder/${reminder_id}`,
      });
      showSuccess('reminder deleted');
      fetchReminders();
    } catch {
      showError('Failed to delete reminder');
    }
  };

  const handleStatusToggle = async (ReminderId: number) => {
    setReminders((prev) =>
      prev.map((item) =>
        item.reminder_id === ReminderId
          ? { ...item, reminder_status: item.reminder_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-reminder/${ReminderId}`,
      });
      showSuccess('Reminder status updated');
    } catch (error) {
      showError('Failed to update status');
      setReminders((prev) =>
        prev.map((item) =>
          item.reminder_id === ReminderId
            ? { ...item, reminder_status: item.reminder_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Reminders>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Reminders> = [

    {
      title: 'Trail',
      dataIndex: 'reminder_trial',
      key: 'reminder_trial',
      filters: Array.from(new Set(reminder.map((s) => s.reminder_trial))).map((name) => ({
        text: dayjs(name).format('DD-MM-YYYY'),
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.reminder_trial || null,
      onFilter: (value, record) => record.reminder_trial.includes(value as string),
      sorter: (a, b) => dayjs(a.reminder_trial).unix() - dayjs(b.reminder_trial).unix(),
      sortOrder: sortedInfo.columnKey === 'reminder_trial' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => (
        <div>
          {record.reminder_trial && dayjs(record.reminder_trial).isValid()
            ? dayjs(record.reminder_trial).format('DD-MM-YYYY')
            : '-'}
        </div>
      ),

    },
    {
      title: 'Delivery',
      dataIndex: 'reminder_delivery',
      key: 'reminder_delivery',
      filters: Array.from(new Set(reminder.map((s) => s.reminder_delivery))).map((name) => ({
        text: dayjs(name).format('DD-MM-YYYY'),
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.reminder_delivery || null,
      onFilter: (value, record) => record.reminder_delivery.includes(value as string),
      sorter: (a, b) => dayjs(a.reminder_delivery).unix() - dayjs(b.reminder_delivery).unix(),
      sortOrder: sortedInfo.columnKey === 'reminder_delivery' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => (
        <div>
          {record.reminder_delivery && dayjs(record.reminder_delivery).isValid()
            ? dayjs(record.reminder_delivery).format('DD-MM-YYYY')
            : '-'}
        </div>
      ),

    },
    {
      title: 'Tailor Name',
      dataIndex: 'ledger_first_name',
      key: 'ledger_first_name',
      filters: Array.from(
        new Set(reminder.map((s: Reminders) => s.ledger_first_name || ''))
      ).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.ledger_first_name || null,
      onFilter: (value, record) =>
        (record.ledger_first_name || '').includes(value as string),
      sorter: (a, b) =>
        (a.ledger_first_name || '').localeCompare(b.ledger_first_name || ''),
      sortOrder:
        sortedInfo.columnKey === 'ledger_first_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => (
        <div>
          {(record.ledger_first_name || '') +
            ' ' +
            (record.ledger_last_name || '')}
        </div>
      ),
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Reminders) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.reminder_status}
                onChange={() => handleStatusToggle(record.reminder_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this reminder?"
              onConfirm={() => handleDelete(record.reminder_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Reminder</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Reminder
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Reminders>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="reminder_id"
          dataSource={reminder.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 11 }}
          locale={{ emptyText: 'No reminder found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, reminder.length)} of{' '}
                {reminder.length}
              </span>
              <Pagination
                {...pagination}
                total={reminder.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <ReminderModel
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default Reminder;